import { Box, Flex, Input, InputGroup, Show } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { useChangePassword } from '@/hooks/service-hooks/auth';
import { useAuthStore } from '@/store';

const passwordSchema = yup.object({
	password: yup
		.string()
		.required('New password is required')
		.min(8, 'Password must be at least 8 characters long')
		.matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
		.matches(/[a-z]/, 'Password must contain at least one lowercase letter')
		.matches(/\d/, 'Password must contain at least one number')
		.matches(
			/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
			'Password must contain at least one special character',
		),
	confirmPassword: yup
		.string()
		.required('Please confirm your new password')
		.oneOf([yup.ref('password')], 'Passwords must match'),
});

type PasswordFormData = yup.InferType<typeof passwordSchema>;

interface FirstLoginChangePasswordFormProps {
	onContinue: () => void;
}

const FirstLoginChangePasswordForm = ({
	onContinue,
}: FirstLoginChangePasswordFormProps) => {
	const accessToken = useAuthStore((state) => state.accessToken);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<PasswordFormData>({
		resolver: yupResolver(passwordSchema),
		mode: 'onSubmit',
	});

	const { mutateAsync: changePassword, isLoading } = useChangePassword(
		(data) => {
			toast.success(data.message || 'Password updated successfully.');
			onContinue();
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message ||
					'Failed to update password. Please try again.',
			);
		},
	);

	const onSubmit = async (data: PasswordFormData) => {
		if (!accessToken) {
			toast.error('Your session has expired. Please login again.');
			return;
		}

		await changePassword({
			password: data.password,
			accessToken,
		});
	};

	return (
		<Box>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Flex flexDirection="column" gap={4}>
					<Flex flexDirection="column" gap={1}>
						<FaceliftText
							fontSize="xl"
							fontWeight="bold"
							color="brand.textDark"
							textAlign="start"
						>
							Create a New Password
						</FaceliftText>
						<FaceliftText
							fontSize="sm"
							color="brand.textDark"
							textAlign="start"
						>
							Before continuing, please create a new password for your account.
						</FaceliftText>
					</Flex>

					<Box>
						<InputGroup
							endElement={
								<FaceliftIconButton
									bg="transparent"
									variant="ghost"
									aria-label="toggle new password visibility"
									size="xs"
									onClick={() => setShowPassword(!showPassword)}
									_hover={{
										bg: { _dark: 'gray.800', _light: 'gray.300' },
									}}
									color={{ _dark: 'brand', _light: 'brand.500' }}
								>
									{showPassword ? <LuEye size={16} /> : <LuEyeOff size={16} />}
								</FaceliftIconButton>
							}
						>
							<Input
								disabled={isLoading}
								{...register('password')}
								variant="outline"
								bg="brand.inputBg"
								size="md"
								placeholder="New password"
								_placeholder={{
									color: { _dark: 'brand.lightGray' },
								}}
								type={showPassword ? 'text' : 'password'}
								borderColor={errors.password ? 'brand.errors' : undefined}
							/>
						</InputGroup>
						<Show when={errors.password}>
							<FaceliftText color="brand.errors" fontSize="xs" mt={1} ml={1}>
								{errors.password?.message}
							</FaceliftText>
						</Show>
					</Box>

					<Box>
						<InputGroup
							endElement={
								<FaceliftIconButton
									bg="transparent"
									variant="ghost"
									aria-label="toggle confirm password visibility"
									size="xs"
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									_hover={{
										bg: { _dark: 'gray.800', _light: 'gray.300' },
									}}
									color={{ _dark: 'brand', _light: 'brand.500' }}
								>
									{showConfirmPassword ? (
										<LuEye size={16} />
									) : (
										<LuEyeOff size={16} />
									)}
								</FaceliftIconButton>
							}
						>
							<Input
								disabled={isLoading}
								{...register('confirmPassword')}
								variant="outline"
								bg="brand.inputBg"
								size="md"
								placeholder="Confirm new password"
								_placeholder={{
									color: { _dark: 'brand.lightGray' },
								}}
								type={showConfirmPassword ? 'text' : 'password'}
								borderColor={errors.confirmPassword ? 'brand.errors' : undefined}
							/>
						</InputGroup>
						<Show when={errors.confirmPassword}>
							<FaceliftText color="brand.errors" fontSize="xs" mt={1} ml={1}>
								{errors.confirmPassword?.message}
							</FaceliftText>
						</Show>
					</Box>

					<FaceliftText fontSize="xs" color="brand.textDark" textAlign="start">
						Your password must be at least 8 characters and include uppercase,
						lowercase, a number, and a special character.
					</FaceliftText>

					<FaceliftButton
						mt={2}
						type="submit"
						borderRadius="6px"
						size="md"
						transition="all 0.3s ease"
						_hover={{
							boxShadow: '0 0 10px 3px',
							border: 'none',
							transform: 'scale(1.02)',
						}}
						letterSpacing="widest"
						loading={isLoading}
						disabled={isLoading}
					>
						Update Password and Continue
					</FaceliftButton>
				</Flex>
			</form>
		</Box>
	);
};

export default FirstLoginChangePasswordForm;
